import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { orderContractModel, OrderWithSellerInfo } from '../pageModels/OrderContractModel';
import { CachedImage } from '../../components/CachedImage';
import Loading from '../../components/Loading/Loading';
import { globalStore } from '../../GlobalStore'; 
import {  
  UserOutlined,  
  ShoppingCartOutlined,
  FilePdfOutlined,
  ThunderboltOutlined,
  CopyOutlined
} from '@ant-design/icons';
import SellerLevelTag from '../../components/SellerLevelTag';
import CreditShow from '../../components/CreditShow';
import { OrderContractStyle } from './OrderContractStyle';
import { Pagination } from 'antd';
import './OrderContract.css';
import { OrderStatus } from '../../models/OrderContract';

const ORDER_STATUS_OPTIONS: { value: number; label: string; color: string }[] = [
  { value: OrderStatus.PENDING_PAYMENT, label: '待支付', color: '#faad14' },
  { value: OrderStatus.PAID_PENDING_SERVICE, label: '待开启服务', color: '#1890ff' },
  { value: OrderStatus.SERVICE_IN_PROGRESS, label: '服务进行中', color: '#52c41a' },
  { value: OrderStatus.PENDING_ACCEPTANCE, label: '待验收', color: '#722ed1' },
  { value: OrderStatus.COMPLETED, label: '已完成', color: '#52c41a' },
  { value: OrderStatus.CANCELLED, label: '已取消', color: '#ff4d4f' },
];

const OrderContract: React.FC = observer(() => {
  const navigate = useNavigate();
  const initialized = globalStore.initialized;
  const isLoggedIn = globalStore.isLoggedIn;

  useEffect(() => {
    if (!initialized) return;
    if (!isLoggedIn) {
      globalStore.loginModalVisible = true;
      navigate('/', { replace: true });
    }
  }, [initialized, isLoggedIn, navigate]);

  useEffect(() => {
    // 滚动到页面顶部
    window.scrollTo(0, 0);
    
    // 设置导航菜单选中状态和强制白色Header
    globalStore.selectedMenuKey = 'order-contract';
    globalStore.forceWhiteHeader = true;
    
    if (globalStore.isLoggedIn) {
      orderContractModel.fetchOrders();
    }
    return () => {
      orderContractModel.reset();
      // 离开页面时重置
      globalStore.forceWhiteHeader = false;
    };
  }, []);

  // 监听登录状态变化，登录后自动获取订单列表
  useEffect(() => {
    if (globalStore.isLoggedIn && globalStore.selectedMenuKey === 'order-contract') {
      orderContractModel.fetchOrders();
    }
  }, [globalStore.isLoggedIn, globalStore.userInformationChangeTrigger]);

  const handleOrderClick = (order: OrderWithSellerInfo) => {
    navigate(`/order-contract/${order.orderId}`);
  };

  const getSpecificationText = (type: number) => {
    switch (type) {
      case 0: return '试用版';
      case 1: return '标准版';
      default: return '未知';
    }
  };

  const getStatusText = (status: number) => {
    return ORDER_STATUS_OPTIONS.find(item => item.value === status)?.label ?? '未知';
  };

  const getStatusColor = (status: number) => {
    return ORDER_STATUS_OPTIONS.find(item => item.value === status)?.color ?? '#8c8c8c';
  };

  const getContractCounts = (order: OrderWithSellerInfo) => {
    const pdfCount = order.pdfContractList?.length || 0;
    const quickCount = order.quickContractList?.length || 0;
    return { pdfCount, quickCount, total: pdfCount + quickCount };
  };

  const formatOrderMonthDay = (orderTime: Date | string): string => {
    const date = orderTime instanceof Date ? orderTime : new Date(orderTime);
    if (isNaN(date.getTime())) return '';
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  };

  if (orderContractModel.loading) {
    return <Loading />;
  }

  return (
    <div style={{ 
      padding: '80px 24px 24px', 
      minHeight: '100vh',
      background: '#f5f5f5'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* 页面标题 */}
        <div style={{ 
          marginBottom: '32px',
          textAlign: 'left'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px'
          }}>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: '400', 
              color: '#0b1220',
              margin: '0'
            }}>
              订单合同
            </h1>
            <p style={{ 
              fontSize: '16px', 
              color: '#5b6472',
              margin: '0'
            }}>
               在Web端管理PDF合同会更为方便。
            </p>
          </div>
        </div>

        {/* 订单列表 */}
        {orderContractModel.orders.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '80px 20px',
            background: '#fff',
            borderRadius: OrderContractStyle.cardBorderRadius,
            border: '1px solid #e8e8e8'
          }}>
            <ShoppingCartOutlined style={{ 
              fontSize: '48px', 
              color: '#d9d9d9',
              marginBottom: '16px'
            }} />
            <div style={{ 
              fontSize: '16px', 
              color: '#8c8c8c',
              marginBottom: '8px'
            }}>
              暂无订单
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#bfbfbf'
            }}>
              您还没有任何订单记录
            </div>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[{ value: null as number | null, label: '全部' }, ...ORDER_STATUS_OPTIONS].map((item) => {
                  const active = orderContractModel.statusFilter === item.value;
                  return (
                    <button
                      key={item.label}
                      onClick={() => orderContractModel.setStatusFilter(item.value)}
                      style={{
                        padding: '6px 14px',
                        fontSize: '13px',
                        border: 'none',
                        borderRadius: OrderContractStyle.buttonBorderRadius,
                        cursor: 'pointer',
                        background: active ? '#0b1220' : '#e9ebef',
                        color: active ? '#fff' : '#5b6472',
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
              {orderContractModel.filteredOrders.length > 0 && (
                <Pagination
                  className="order-contract-pagination"
                  current={orderContractModel.page}
                  pageSize={orderContractModel.pageSize}
                  total={orderContractModel.filteredOrders.length}
                  showSizeChanger={false}
                  onChange={(page) => {
                    orderContractModel.setPage(page);
                    window.scrollTo(0, 0);
                  }}
                />
              )}
            </div>
            {orderContractModel.pagedOrders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '80px 20px',
                background: '#fff',
                borderRadius: OrderContractStyle.cardBorderRadius,
              }}>
                <div style={{ fontSize: '16px', color: '#8c8c8c' }}>
                  该状态下暂无订单
                </div>
              </div>
            ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {orderContractModel.pagedOrders.map((order) => {
              const { pdfCount, quickCount, total } = getContractCounts(order);
              const orderMonthDay = formatOrderMonthDay(order.orderTime);
              return (
              <div
                key={order.orderId}
                onClick={() => handleOrderClick(order)}
                style={{
                  background: '#fff',
                  padding: '20px',
                  border: '1px solid #f0f0f0',
                  borderRadius: OrderContractStyle.cardBorderRadius,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';
                  e.currentTarget.style.borderColor = '#f0f0f0';
                }}
              >
                {/* 顶部状态条 */}
                <div style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${getStatusColor(order.status)}, ${getStatusColor(order.status)}80)`
                }} />
                
                {/* 主要内容区域 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* 头部：商品信息和状态 */}
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    {/* 商品缩略图 */}
                    <div style={{ 
                      flexShrink: '0',
                      position: 'relative'
                    }}>
                      <CachedImage
                        url={order.productSnapshot.headImageUrl}
                        style={{
                          width: '72px',
                          height: '72px',
                          objectFit: 'cover',
                          borderRadius: OrderContractStyle.imageBorderRadius,
                          border: '2px solid #f8f8f8',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        alt={order.productSnapshot.name}
                      />
                    </div>

                    {/* 右侧信息区域 - 分为3行 */}
                    <div style={{ 
                      flex: '1', 
                      minWidth: '0',
                      height: '72px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      {/* 第一行：商品标题 */}
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1a1a1a',
                        lineHeight: '1.4',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {order.productSnapshot.name}
                      </div>
                      
                      {/* 第二行：卖家头像、名称、等级、下单日期 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          color: '#666',
                          flexShrink: 0
                        }}>
                          卖家：
                        </span>
                        {/* 卖家头像 */}
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          overflow: 'hidden',
                          borderRadius: OrderContractStyle.avatarBorderRadius,
                          border: '1px solid #f0f0f0',
                          flexShrink: 0
                        }}>
                          {order.sellerInfo?.avatarUrl ? (
                            <CachedImage
                              url={order.sellerInfo.avatarUrl}
                              enablePreview={false}
                              style={{
                                display: 'block',
                                width: '20px',
                                height: '20px',
                                objectFit: 'cover'
                              }}
                              alt="卖家头像"
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <UserOutlined style={{ 
                                fontSize: '10px', 
                                color: '#fff'
                              }} />
                            </div>
                          )}
                        </div>
                        
                        {/* 卖家昵称和等级 */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          minWidth: 0
                        }}>
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {order.sellerInfo?.nickname || '未知用户'}
                          </span>
                          {order.sellerInfo?.sellerLevel && order.sellerInfo?.sellerCredit && (
                            <>  
                              <SellerLevelTag 
                                level={order.sellerInfo.sellerLevel} 
                                creditLevel={order.sellerInfo.sellerCredit}
                                size="small"
                              />
                              <CreditShow credit={order.sellerInfo.sellerCredit} fontSize={12} />
                            </> 
                          )}
                        </div>
                        {orderMonthDay && (
                          <span style={{
                            fontSize: '12px',
                            color: '#999',
                            flexShrink: 0,
                            marginLeft: '8px'
                          }}>
                            {orderMonthDay}
                          </span>
                        )}
                      </div>

                      {/* 第三行：规格、数量、价格 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                      }}>
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#666',
                          fontWeight: '500',
                          padding: '2px 6px',
                          borderRadius: OrderContractStyle.tagBorderRadius,
                          background: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}>
                          {getSpecificationText(order.specificationType)}
                        </span>
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#666',
                          fontWeight: '500'
                        }}>
                          数量: {order.quantity}
                        </span>
                        <span style={{ 
                          fontSize: '16px', 
                          fontWeight: '700', 
                          color: '#ff6b35'
                        }}>
                          ¥{order.price.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* 订单状态 */}
                    <div style={{ 
                      fontSize: '11px',
                      fontWeight: '600',
                      color: getStatusColor(order.status),
                      flexShrink: '0',
                      textAlign: 'center',
                      minWidth: '60px'
                    }}>
                      {getStatusText(order.status)}
                    </div>
                  </div>

                  {/* 底部信息行 */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: '12px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    {/* 合同数量 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '6px 10px',
                        borderRadius: OrderContractStyle.badgeBorderRadius,
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef'
                      }}>
                        <FilePdfOutlined style={{ fontSize: '12px', color: '#dc3545' }} />
                        <span style={{ fontSize: '12px', color: '#495057', fontWeight: '500' }}>
                          PDF {pdfCount}
                        </span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '6px 10px',
                        borderRadius: OrderContractStyle.badgeBorderRadius,
                        background: '#fff7e6',
                        border: '1px solid #ffd591'
                      }}>
                        <ThunderboltOutlined style={{ fontSize: '12px', color: '#fa8c16' }} />
                        <span style={{ fontSize: '12px', color: '#ad6800', fontWeight: '500' }}>
                          闪电 {quickCount}
                        </span>
                      </div>
                      <span style={{ fontSize: '12px', color: '#adb5bd' }}>
                        共 {total} 份
                      </span>
                    </div>
                    
                    {/* 订单ID和复制按钮 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px'
                    }}>
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#adb5bd',
                        fontFamily: 'Monaco, Menlo, monospace'
                      }}>
                        {order.orderId.slice(-8)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(order.orderId);
                          // 这里可以添加复制成功的提示
                          alert('订单ID已复制到剪贴板');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          borderRadius: OrderContractStyle.buttonBorderRadius,
                          cursor: 'pointer',
                          padding: '2px',
                          color: '#999',
                          fontSize: '12px'
                        }}
                        title="复制订单ID"
                      >
                        <CopyOutlined />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default OrderContract; 